import { SAMPLE_WORKFLOWS } from "@/data/aiToolkit";
import { WORKFLOW_TEMPLATES } from "@/data/workflowTemplates";
import { aiWorkflowsService } from "@/services/aiWorkflowsService";
import { workflowRunsService } from "@/services/workflowRunsService";
import type { Workflow } from "@/types/aiToolkit";
import type { WorkflowRun } from "@/types/workflowEngine";

const BUILTIN_WORKFLOWS = [...WORKFLOW_TEMPLATES, ...SAMPLE_WORKFLOWS];

function workflowFromVersion(
  version: NonNullable<Awaited<ReturnType<typeof workflowRunsService.getVersion>>>,
  run: WorkflowRun
): Workflow {
  return {
    id: run.workflowId,
    name: "Workflow run",
    description: "",
    tier: "workflow",
    access: "pro",
    icon: "GitBranch",
    color: "bg-blue-500",
    category: "Workflow",
    trigger: version.trigger,
    nodes: version.nodes,
    edges: version.edges,
    entryNodeId: version.entryNodeId ?? version.nodes[0]?.id ?? "",
    status: "active",
    createdAt: version.publishedAt,
    createdBy: "system",
  };
}

function findBuiltinById(templateId: string): Workflow | null {
  return (
    BUILTIN_WORKFLOWS.find((w) => w.id === templateId) ??
    BUILTIN_WORKFLOWS.find((w) => w.tags?.some((t) => t === `tpl:${templateId}`)) ??
    null
  );
}

function findBuiltinByRun(run: WorkflowRun): Workflow | null {
  const byTplTag = BUILTIN_WORKFLOWS.find((w) =>
    w.tags?.some((t) => t === `tpl:${run.workflowId}`)
  );
  if (byTplTag) return byTplTag;

  return (
    BUILTIN_WORKFLOWS.find((w) => w.id === run.workflowId) ??
    BUILTIN_WORKFLOWS.find((w) => w.name.toLowerCase().includes("gmail spam")) ??
    null
  );
}

function builtinFromDbTags(tags: string[] | undefined): Workflow | null {
  const tplTag = tags?.find((t) => t.startsWith("tpl:"));
  if (!tplTag) return null;
  return findBuiltinById(tplTag.slice(4));
}

export async function resolveWorkflowForRun(options: {
  propWorkflow?: Workflow | null;
  run: WorkflowRun;
}): Promise<Workflow | null> {
  if (options.propWorkflow?.nodes?.length) {
    return options.propWorkflow;
  }

  const dbWorkflow = await aiWorkflowsService.getWorkflowById(options.run.workflowId);
  if (dbWorkflow?.nodes?.length) {
    return dbWorkflow;
  }

  const fromDbTpl = builtinFromDbTags(dbWorkflow?.tags);
  if (fromDbTpl) return fromDbTpl;

  if (options.run.workflowVersionId) {
    const version = await workflowRunsService.getVersion(options.run.workflowVersionId);
    if (version?.nodes?.length) {
      return workflowFromVersion(version, options.run);
    }
  }

  return findBuiltinByRun(options.run);
}
